import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';
import ExpressCheckoutButton from '../components/ExpressCheckoutButton';

const ProductDetail = ({ deviceId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  // Whether Stripe Express Checkout has any wallets to show on this device.
  // null = unknown (still loading), true = at least one wallet, false = none.
  const [walletsAvailable, setWalletsAvailable] = useState(null);
  // When true, show BACK image instead of FRONT (hover on desktop, tap on mobile).
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await api.getProduct(id);
      setProduct(data);
      
      if (data.variants && data.variants.length > 0) {
        const firstVariant = data.variants[0];
        setSelectedColor(firstVariant.color);
        setSelectedSize(firstVariant.size);
        setSelectedVariant(firstVariant);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setShowBack(false);  // reset front/back when switching colors
    // First try to find variant with same size
    let variant = product.variants.find(v => v.color === color && v.size === selectedSize);
    // If not found, get any variant with this color
    if (!variant) {
      variant = product.variants.find(v => v.color === color);
      if (variant) {
        setSelectedSize(variant.size);
      }
    }
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  // Get color swatch URL for a color
  const getColorSwatch = (color) => {
    const variant = product.variants.find(v => v.color === color);
    return variant ? variant.color_swatch_url : null;
  };

  // Lookup { front, back } image URLs for a color from product.image_tags.
  // Returns { front: string|null, back: string|null }.
  const getAnglesForColor = (color) => {
    const tags = (product && product.image_tags) || [];
    let front = null, back = null;
    for (const t of tags) {
      if (!t || t.color !== color) continue;
      if (t.angle === 'front' && !front) front = t.url;
      else if (t.angle === 'back' && !back) back = t.url;
    }
    return { front, back };
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    // First try to find variant with same color
    let variant = product.variants.find(v => v.color === selectedColor && v.size === size);
    // If not found, get any variant with this size
    if (!variant) {
      variant = product.variants.find(v => v.size === size);
      if (variant) {
        setSelectedColor(variant.color);
      }
    }
    if (variant) {
      setSelectedVariant(variant);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    
    try {
      setAdding(true);
      await api.addToCart({
        deviceId,
        productId: product._id,
        variantId: selectedVariant.variant_id,
        quantity,
        color: selectedColor,
        size: selectedSize
      });
      // Show modal instead of navigating to cart
      setShowCartModal(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  // One-tap "BUY NOW": adds the selected variant to the cart and jumps
  // straight to /checkout. This is the device-agnostic fallback for any
  // browser that can't render Apple Pay / Google Pay / Link inline.
  const handleBuyNow = async () => {
    if (!selectedVariant || buyingNow) return;
    try {
      setBuyingNow(true);
      await api.addToCart({
        deviceId,
        productId: product._id,
        variantId: selectedVariant.variant_id,
        quantity,
        color: selectedColor,
        size: selectedSize,
      });
      navigate('/checkout');
    } catch (error) {
      console.error('Buy Now failed:', error);
      alert('Could not start checkout. Please try again.');
      setBuyingNow(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <TopNav deviceId={deviceId} />
        <div style={{ textAlign: 'center' }}>
          <img src="/logo.png" alt="Loading" style={{ width: '80px', height: '80px', imageRendering: 'pixelated' }} />
          <p style={{ color: '#4C956C', marginTop: '20px' }}>LOADING...</p>
        </div>
        <BottomNav currentPath="/product" />
      </div>
    );
  }

  if (!product) {
    return <div style={{ padding: '20px' }}>Product not found</div>;
  }

  const uniqueColors = [...new Set(product.variants.map(v => v.color))];
  const uniqueSizes = [...new Set(product.variants.map(v => v.size))];

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3', paddingBottom: '100px' }}>
      <TopNav deviceId={deviceId} currentPath="/product" />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <Link to="/" style={{ color: '#2C6E49', textDecoration: 'none', fontSize: '12px', marginBottom: '20px', display: 'inline-block' }}>
          {'<<< BACK TO SHOP'}
        </Link>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '30px', marginTop: '20px' }}>
          <div className="retro-card" style={{ background: '#FFF', padding: '20px' }}>
            {(() => {
              const { front, back } = getAnglesForColor(selectedColor);
              const variantImg = selectedVariant && selectedVariant.image_url;
              const fallback = (product.images && product.images[0]) || null;
              // FRONT = tagged front || variant image || first product image
              const frontUrl = front || variantImg || fallback;
              // BACK is only valid when explicitly tagged (one-image flows stay unchanged)
              const backUrl = back || null;
              const hasFlip = !!(frontUrl && backUrl);
              const currentUrl = hasFlip && showBack ? backUrl : frontUrl;
              return (
                <div
                  data-testid="product-detail-image-frame"
                  onMouseEnter={() => { if (hasFlip) setShowBack(true); }}
                  onMouseLeave={() => { if (hasFlip) setShowBack(false); }}
                  onClick={(e) => {
                    // On touch devices (no hover), tapping the image flips it.
                    if (!hasFlip) return;
                    const noHover = window.matchMedia && window.matchMedia('(hover: none)').matches;
                    if (noHover) {
                      e.preventDefault();
                      setShowBack(v => !v);
                    }
                  }}
                  style={{
                    background: '#F7F6D8',
                    border: '3px solid #000',
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: hasFlip ? 'pointer' : 'default',
                  }}
                >
                  {currentUrl ? (
                    <img
                      key={currentUrl}
                      src={currentUrl}
                      alt={`${product.name} - ${selectedColor}${hasFlip ? (showBack ? ' back' : ' front') : ''}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 180ms ease' }}
                    />
                  ) : (
                    <span style={{ fontSize: '64px' }}>👕</span>
                  )}
                  {hasFlip && (
                    <div
                      data-testid="product-detail-flip-badge"
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        background: '#000',
                        color: '#FFC857',
                        padding: '4px 8px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        letterSpacing: '0.1em',
                        border: '2px solid #FFC857',
                        boxShadow: '2px 2px 0 0 #FFC857',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    >
                      {showBack ? 'BACK' : 'FRONT'} · {window.matchMedia && window.matchMedia('(hover: none)').matches ? 'TAP TO FLIP' : 'HOVER TO FLIP'}
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <span style={{ fontSize: '11px', color: '#4C956C', fontWeight: 'bold' }}>
                {selectedColor} • {selectedSize}
              </span>
            </div>
          </div>
          
          <div className="retro-card" style={{ background: '#FFF', padding: '30px' }}>
            <h1 style={{ fontSize: '24px', color: '#2C6E49', marginBottom: '10px' }}>{product.name}</h1>
            <p style={{ fontSize: '12px', color: '#4C956C', marginBottom: '20px' }}>{product.category}</p>
            <p style={{ fontSize: '32px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '20px' }}>${selectedVariant ? selectedVariant.price.toFixed(2) : product.base_price.toFixed(2)}</p>
            
            {product.description && (
              <p style={{ fontSize: '12px', color: '#4B4B4B', marginBottom: '30px', lineHeight: '1.6' }}>{product.description}</p>
            )}
            
            <div style={{ marginBottom: '25px' }}>
              <p style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', fontWeight: 'bold' }}>[ COLOR ]</p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {uniqueColors.map((color) => {
                  const swatchUrl = getColorSwatch(color);
                  const isSelected = selectedColor === color;
                  
                  return (
                    <button
                      key={color}
                      onClick={() => handleColorChange(color)}
                      className="retro-btn"
                      style={{
                        background: isSelected ? '#2C6E49' : '#FFF',
                        color: isSelected ? '#FEFEE3' : '#2C2C2C',
                        padding: swatchUrl ? '8px' : '10px 20px',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: isSelected ? '3px solid #2C6E49' : '3px solid #000',
                        position: 'relative'
                      }}
                      data-testid={`color-button-${color}`}
                    >
                      {swatchUrl && (
                        <div style={{
                          width: '32px',
                          height: '32px',
                          border: isSelected ? '2px solid #FEFEE3' : '2px solid #2C2C2C',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          <img 
                            src={swatchUrl} 
                            alt={color}
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                        </div>
                      )}
                      <span>{color}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div style={{ marginBottom: '25px' }}>
              <p style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', fontWeight: 'bold' }}>[ SIZE ]</p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {uniqueSizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => handleSizeChange(size)}
                    className="retro-btn"
                    style={{
                      background: selectedSize === size ? '#2C6E49' : '#FFF',
                      color: selectedSize === size ? '#FEFEE3' : '#2C2C2C',
                      padding: '10px 20px',
                      fontSize: '11px',
                      minWidth: '60px'
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            
            <div style={{ marginBottom: '30px' }}>
              <p style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', fontWeight: 'bold' }}>[ QUANTITY ]</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="retro-btn"
                  style={{ background: '#FFF', color: '#2C2C2C', padding: '10px 20px', fontSize: '18px' }}
                >
                  -
                </button>
                <span style={{ fontSize: '20px', fontWeight: 'bold', minWidth: '40px', textAlign: 'center' }}>{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="retro-btn"
                  style={{ background: '#FFF', color: '#2C2C2C', padding: '10px 20px', fontSize: '18px' }}
                >
                  +
                </button>
              </div>
            </div>
            
            <button
              onClick={handleAddToCart}
              disabled={adding || !selectedVariant}
              className="retro-btn"
              style={{
                background: '#2C6E49',
                color: '#FEFEE3',
                padding: '20px 40px',
                fontSize: '14px',
                width: '100%',
                opacity: adding ? 0.6 : 1,
                cursor: adding ? 'not-allowed' : 'pointer'
              }}
            >
              {adding ? 'ADDING...' : 'ADD TO CART >>>'}
            </button>

            {/* Express Checkout (Apple Pay / Google Pay / Link) + always-available BUY NOW fallback */}
            {selectedVariant && (
              <div style={{ marginTop: '14px' }} data-testid="product-express-checkout">
                {/* Caption only renders once we know whether wallets are usable
                    AND at least one wallet is available. Prevents a stranded
                    "OR BUY NOW" caption on devices like older iOS Safari that
                    don't show any Stripe wallet buttons. */}
                {walletsAvailable === true && (
                  <p style={{ fontSize: '10px', color: '#888', letterSpacing: '0.18em', textAlign: 'center', marginBottom: '8px' }}>
                    — OR PAY WITH —
                  </p>
                )}
                <ExpressCheckoutButton
                  mode="product"
                  items={[{
                    product_id: product._id || product.id,
                    variant_id: selectedVariant.variant_id,
                    name: product.name,
                    color: selectedVariant.color || selectedColor,
                    size: selectedVariant.size,
                    quantity: 1,
                    price: selectedVariant.price ?? product.base_price ?? 0,
                    image_url: selectedVariant.image_url || (product.images && product.images[0]),
                  }]}
                  onAvailability={(hasAny) => setWalletsAvailable(hasAny)}
                  compact
                />

                {/* Always-on BUY NOW fallback — works on every browser/device */}
                <button
                  onClick={handleBuyNow}
                  disabled={buyingNow || adding}
                  data-testid="buy-now-fallback-btn"
                  className="retro-btn"
                  style={{
                    marginTop: walletsAvailable === true ? '12px' : '14px',
                    background: '#000',
                    color: '#FEFEE3',
                    padding: '18px 24px',
                    fontSize: '13px',
                    width: '100%',
                    letterSpacing: '0.12em',
                    border: '3px solid #000',
                    boxShadow: '4px 4px 0 0 #2C6E49',
                    opacity: (buyingNow || adding) ? 0.6 : 1,
                    cursor: (buyingNow || adding) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {buyingNow ? 'STARTING CHECKOUT…' : 'BUY NOW →'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Added to Cart Modal */}
      {showCartModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowCartModal(false)}
        >
          <div 
            style={{
              background: '#0a0a0a',
              border: '4px solid #4AE54A',
              borderRadius: '8px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 0 30px rgba(74, 229, 74, 0.5)',
              animation: 'fadeIn 0.3s ease-in'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto',
                background: '#2C6E49',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '3px solid #4AE54A',
                fontSize: '40px'
              }}>
                ✓
              </div>
            </div>
            
            {/* Message */}
            <h2 style={{
              color: '#4AE54A',
              fontSize: '24px',
              textAlign: 'center',
              marginBottom: '10px',
              fontFamily: 'Press Start 2P, monospace',
              textShadow: '0 0 10px rgba(74, 229, 74, 0.5)'
            }}>
              ITEM ADDED!
            </h2>
            
            <p style={{
              color: '#FEFEE3',
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: '30px',
              fontFamily: 'Courier New, monospace'
            }}>
              {product?.name} has been added to your cart
            </p>
            
            {/* Product Summary */}
            <div style={{
              background: '#1a1a1a',
              border: '2px solid #2C6E49',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '25px',
              display: 'flex',
              gap: '15px',
              alignItems: 'center'
            }}>
              {selectedVariant?.images && selectedVariant.images.length > 0 && (
                <img 
                  src={selectedVariant.images[0]}
                  alt={product?.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover',
                    border: '2px solid #4AE54A',
                    borderRadius: '4px'
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <p style={{ color: '#4AE54A', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {product?.name}
                </p>
                <p style={{ color: '#FEFEE3', fontSize: '11px', marginBottom: '3px' }}>
                  Color: {selectedColor} | Size: {selectedSize}
                </p>
                <p style={{ color: '#FEFEE3', fontSize: '11px' }}>
                  Quantity: {quantity}
                </p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              <button
                onClick={() => navigate('/cart')}
                className="retro-btn"
                style={{
                  background: '#2C6E49',
                  color: '#FEFEE3',
                  padding: '15px',
                  fontSize: '14px',
                  border: '3px solid #4AE54A',
                  width: '100%',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                🛒 VIEW CART
              </button>
              
              <button
                onClick={() => setShowCartModal(false)}
                className="retro-btn"
                style={{
                  background: 'transparent',
                  color: '#4AE54A',
                  padding: '15px',
                  fontSize: '14px',
                  border: '3px solid #2C6E49',
                  width: '100%',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ◄ CONTINUE SHOPPING
              </button>
            </div>
          </div>
        </div>
      )}
      
      <BottomNav currentPath="/product" />
    </div>
  );
};

export default ProductDetail;
